import { useState, useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-calendar-check-line',
    title: 'Schedule Your Free Consultation',
    description:
      'Reach out and tell us a little about where you are and where you want to go. There is no pressure and no commitment — just a conversation.',
  },
  {
    number: '02',
    icon: 'ri-lightbulb-line',
    title: 'Get Clarity on Your Next Steps',
    description:
      'We will talk through your goals, challenges, and opportunities for growth — so you leave with a clear picture of what is possible.',
  },
  {
    number: '03',
    icon: 'ri-seedling-line',
    title: 'Start Building Your Brand New You',
    description:
      'With the right strategy and support, you can begin moving forward with confidence toward the life you truly want.',
  },
];

export default function HowItWorksSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="how-it-works" className="bg-[#FAF8F5] py-24 md:py-32" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div
          className={`text-center mb-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <span
            className="text-xs font-semibold tracking-widest uppercase text-[#D4896A] block mb-4"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            The Process
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#2C2C2C]"
            style={{ fontFamily: '\'Playfair Display\', serif' }}
          >
            Your Journey Can Start in 3 Simple Steps
          </h2>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {/* Connecting Line (desktop only) */}
          <div className="hidden md:block absolute top-12 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-px border-t-2 border-dashed border-[#C8B8A8]" />

          {steps.map((step, i) => (
            <div
              key={step.number}
              className={`relative flex flex-col items-center md:items-start text-center md:text-left transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              {/* Step Circle */}
              <div className="relative z-10 w-24 h-24 rounded-full bg-white border-2 border-dashed border-[#C8B8A8] flex items-center justify-center mb-8 shrink-0">
                <div className="w-14 h-14 rounded-full bg-[#D4896A] flex items-center justify-center">
                  <i className={`${step.icon} text-white text-2xl`} />
                </div>
              </div>

              {/* Large step number */}
              <span
                className="text-7xl font-bold text-[#E8DDD4] -mt-4 mb-2 leading-none"
                style={{ fontFamily: '\'Playfair Display\', serif' }}
              >
                {step.number}
              </span>

              <h3
                className="text-xl font-bold text-[#2C2C2C] mb-4"
                style={{ fontFamily: '\'Playfair Display\', serif' }}
              >
                {step.title}
              </h3>
              <p
                className="text-base text-[#6B6B6B] leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-16 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <button
            onClick={() => scrollTo('consultation')}
            className="bg-[#D4896A] text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-[#c07a5a] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg hover:-translate-y-0.5"
          >
            Start Your Journey Today
          </button>
        </div>
      </div>
    </section>
  );
}
